import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import {
  StyledHeader,
  StyledHeaderContainer,
  StyledTableContainer,
} from 'helpers/StyledTableComponentsHelper';
import Spacer from 'components/ui/Spacer';
import { AccentColor } from 'helpers/AccentColorHelper';
import TabbedTable from 'components/ui/TabbedTable';
import MobileTable from 'components/ui/MobileTable';
import TableFilters from 'components/ui/TableFilters';
import MediaQuery from 'react-responsive';
import StatusTag from 'components/ui/StatusTag';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { colors } from 'helpers/ColorHelper';
import { getFilteredBatchPayments, clearBatchPayments } from '../../store/actions';
import { media } from 'helpers/MediaHelper';
import BatchPaymentEditing from './LinkedPayments/BatchPaymentEditing';
import AntDrawer from 'components/ui/Drawer';

const ScrollablePanel = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
`;

const EmptyContainer = styled.div`
  width: 366px;
  margin: 100px auto 200px;
  padding: 1rem;
`;

const EmptyTitle = styled.div`
  font-size: 20px;
  text-align: center;
  font-family: 'Circular-Bold';
  margin-bottom: 8px;
`;

const EmptyMessage = styled.div`
  color: ${colors.MED_DARK_GREY};
  text-align: center;
`;

const RestyledHeaderContainer = styled(StyledHeaderContainer)`
  padding: 22px 20px 0;

  ${media.mobile`
    padding-top: 0;
  `};
`;

const RestyledHeader = styled(StyledHeader)`
  ${media.mobile`
    margin-top: -11px;
  `};
`;

class FailedPayments extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      searchActive: false,
      sortKey: '',
      sortOrder: '',
      offset: 0,
      batchPayment: {},
      batchPaymentId: null,
      editBatchPaymentDrawerOpen: false,
    };
  }

  componentDidMount() {
    this.fetchFilteredPayments();
  }

  componentWillUnmount() {
    this.props.clearBatchPayments();
  }

  handleSort = (key, order) => {
    this.setState(
      {
        sortKey: key,
        sortOrder: order,
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleClearFilters = () => {
    this.setState(
      {
        search: '',
        searchActive: false,
        sortKey: '',
        sortOrder: '',
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  handleSubmitFilters = values => {
    this.setState(
      {
        search: values.search,
        searchActive: values.search !== '',
        offset: 0,
      },
      () => {
        this.fetchFilteredPayments();
      }
    );
    if (this.table) {
      this.table.scrollToTop(0);
    }
  };

  fetchFilteredPayments = async () => {
    const { search, searchActive, offset, sortKey, sortOrder } = this.state;
    let filterOptions = { sort_key: sortKey, sort_order: sortOrder };
    if (searchActive) {
      filterOptions.query = search;
    }

    const batchId = this.props.batch.id;

    await this.props.getFilteredBatchPayments(batchId, 'failed', offset, filterOptions);
    this.setState({ offset: this.props.batchPayments.length });
  };

  openEditDrawer = data => {
    const formData = {
      unit_id: data.unit_id,
      cotenant_id: data.cotenant_id,
      amount: data.amount,
      description: data.description,
      payment_method: data.payment_method,
    };
    this.setState({
      batchPayment: formData,
      batchPaymentId: data.original_id || data.id,
      editBatchPaymentDrawerOpen: true,
    });
  };

  closeEditDrawer = () => {
    this.setState({
      batchPayment: {},
      batchPaymentId: null,
      editBatchPaymentDrawerOpen: false,
    });
  };

  render() {
    const columns = [
      {
        title: getTranslatedString('landlordPortal.batches.status'),
        dataKey: 'status',
        sortable: false,
        render: ({ data }) => <StatusTag status={data} />,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.resident'),
        dataKey: 'cotenant_full_name',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.description'),
        dataKey: 'description',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.batchPaymentId'),
        dataKey: 'original_id',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.unit'),
        dataKey: 'unit_number',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.amount'),
        dataKey: 'amount',
        sortable: true,
        width: 300,
      },
      {
        title: getTranslatedString('landlordPortal.batches.dateFailed'),
        dataKey: 'processed_at',
        sortable: true,
        width: 300,
      },
    ];

    if (!this.state.searchActive && this.props.batchPayments.length === 0) {
      return (
        <EmptyContainer>
          <EmptyTitle>{getTranslatedString('landlordPortal.batches.emptyFailedTitle')}</EmptyTitle>
          <EmptyMessage>
            {getTranslatedString('landlordPortal.batches.emptyFailedMessage')}
          </EmptyMessage>
        </EmptyContainer>
      );
    }

    return (
      <ScrollablePanel>
        <RestyledHeaderContainer>
          <RestyledHeader />
          <TableFilters
            filters={{ search: this.state.search }}
            handleSubmitFilters={this.handleSubmitFilters}
            handleClearFilters={this.handleClearFilters}
            filterOptions={[]}
            accentColor={AccentColor()}
            bgColor="white"
          />
        </RestyledHeaderContainer>
        <Row gutter={16}>
          <Col xs={24}>
            <Spacer mt={0}>
              <StyledTableContainer>
                <MediaQuery query="(min-device-width: 750px)">
                  <TabbedTable
                    setRef={ref => {
                      this.table = ref;
                    }}
                    accentColor={AccentColor()}
                    tabs={[]}
                    columns={columns}
                    data={this.props.batchPayments}
                    loadMoreData={this.fetchFilteredPayments}
                    filtersActive={this.state.filtersActive}
                    onRowClick={data => this.openEditDrawer(data)}
                    onColumnClick={data => this.handleSort(data.key, data.order)}
                    searchTotalResultsCount={this.props.searchResultCount}
                  />
                </MediaQuery>
                <MediaQuery query="(max-device-width: 750px)">
                  <MobileTable
                    data={this.props.batchPayments}
                    columns={columns}
                    tag="status"
                    stickyHeaderTop={'107px'}
                    title="cotenant_full_name"
                    fieldsAboveFold={[
                      {
                        title: getTranslatedString('landlordPortal.batches.unit'),
                        key: 'unit_number',
                      },
                      {
                        title: getTranslatedString('landlordPortal.batches.amount'),
                        key: 'amount',
                      },
                    ]}
                    fieldsBelowFold={[
                      {
                        title: getTranslatedString('landlordPortal.batches.description'),
                        key: 'description',
                      },
                      {
                        title: getTranslatedString('landlordPortal.batches.dateFailed'),
                        key: 'processed_at',
                      },
                    ]}
                    onRowClick={data => this.openEditDrawer(data)}
                  />
                </MediaQuery>
              </StyledTableContainer>
            </Spacer>
          </Col>
        </Row>
        <AntDrawer
          maskClosable={false}
          keyboard
          visible={this.state.editBatchPaymentDrawerOpen}
          onClose={this.closeEditDrawer}
          closable={false}
          width={600}
        >
          {this.state.editBatchPaymentDrawerOpen && (
            <BatchPaymentEditing
              batchId={this.props.batch.id}
              batchPaymentId={this.state.batchPaymentId}
              handleClose={this.closeEditDrawer}
              formData={this.state.batchPayment}
              disabled={this.props.batch.status !== 'draft'}
            />
          )}
        </AntDrawer>
      </ScrollablePanel>
    );
  }
}

const mapStateToProps = store => ({
  batchPayments: store.landlord.batches.batchPayments,
});

const actionCreators = {
  getFilteredBatchPayments,
  clearBatchPayments,
};

export default connect(mapStateToProps, actionCreators)(FailedPayments);
